import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Edit, Trash2, MapPin, FileText, Phone, Settings, Info } from 'lucide-react'
import Layout from '../../components/Layout'
import Seo from '../../components/Seo'
import { listingService } from '../../services/listingService'
import { getPropertyTypeLabel, getConditionLabel } from '../../constants/propertyConstants'
import ListingMap from '../../components/ListingMap'

const ListingDetail = () => {
  const navigate = useNavigate()
  const { listingId } = useParams()
  const [listing, setListing] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [activeTab, setActiveTab] = useState('basic')

  // Fetch listing data
  useEffect(() => {
    const fetchListing = async () => {
      try {
        setLoading(true)
        setError(null)

        const data = await listingService.getListing(listingId)
        console.log('Listing data:', data)
        setListing(data)
      } catch (error) {
        console.error('Error fetching listing:', error)
        setError('Chyba při načítání inzerátu')
      } finally {
        setLoading(false)
      }
    }

    if (listingId) {
      fetchListing()
    }
  }, [listingId])

  const handleDelete = async () => {
    if (!window.confirm('Opravdu chcete smazat tento inzerát?')) {
      return
    }

    try {
      await listingService.deleteListing(listingId)
      navigate('/listings')
    } catch (error) {
      console.error('Error deleting listing:', error)
      alert('Chyba při mazání inzerátu')
    }
  }

  const tabs = [
    {
      id: 'basic',
      name: 'Základní informace',
      icon: Info,
      content: listing ? (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-6">Základní informace</h2>

          {/* Address - Single column */}
          <div className="mb-6">
            <div className="text-sm text-gray-600 mb-2">Adresa</div>
            <div className="text-gray-900 font-medium">{listing.address}</div>
          </div>

          {/* Two-column layout for other fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="text-sm text-gray-600 mb-2">Dispozice</div>
              <div className="text-gray-900">{listing.layout || '-'}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600 mb-2">Stav</div>
              <div className="text-gray-900">{listing.condition ? getConditionLabel(listing.condition) : '-'}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600 mb-2">Výměra</div>
              <div className="text-gray-900">{listing.m2 ? `${listing.m2} m²` : '-'}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600 mb-2">Patro</div>
              <div className="text-gray-900">{listing.floor || '-'}</div>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="animate-pulse">
            <div className="h-6 bg-gray-200 rounded mb-6"></div>
            <div className="space-y-4">
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'map',
      name: 'Mapa',
      icon: MapPin,
      content: listing ? (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-6">Mapa</h2>
          <div className="h-96">
            <ListingMap listing={listing} />
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="animate-pulse">
            <div className="h-6 bg-gray-200 rounded mb-6"></div>
            <div className="h-96 bg-gray-200 rounded"></div>
          </div>
        </div>
      ),
    },
    {
      id: 'description',
      name: 'Popis',
      icon: FileText,
      content: listing ? (
        listing.description ? (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-6">Popis</h2>
            <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{listing.description}</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-6">Popis</h2>
            <p className="text-gray-500 italic">Žádný popis nebyl přidán.</p>
          </div>
        )
      ) : (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="animate-pulse">
            <div className="h-6 bg-gray-200 rounded mb-6"></div>
            <div className="space-y-2">
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'contact',
      name: 'Kontakt',
      icon: Phone,
      content: listing ? (
        listing.contactInfo ? (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-6">Kontaktní informace</h2>
            <p className="text-gray-700 leading-relaxed">{listing.contactInfo}</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-6">Kontaktní informace</h2>
            <p className="text-gray-500 italic">Žádné kontaktní informace nebyly přidány.</p>
          </div>
        )
      ) : (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="animate-pulse">
            <div className="h-6 bg-gray-200 rounded mb-6"></div>
            <div className="space-y-2">
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded w-2/3"></div>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'metadata',
      name: 'Metadata',
      icon: Settings,
      content: listing ? (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-6">Metadata</h2>
          <div className="space-y-4 text-sm">
            <div className="flex justify-between items-center py-2 border-b border-gray-100">
              <span className="font-medium text-gray-600">Vytvořeno:</span>
              <span className="text-gray-900">
                {new Date(listing.createdAt).toLocaleDateString('cs-CZ')} v {new Date(listing.createdAt).toLocaleTimeString('cs-CZ')}
              </span>
            </div>
            {listing.updatedAt && listing.updatedAt !== listing.createdAt && (
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="font-medium text-gray-600">Upraveno:</span>
                <span className="text-gray-900">
                  {new Date(listing.updatedAt).toLocaleDateString('cs-CZ')} v {new Date(listing.updatedAt).toLocaleTimeString('cs-CZ')}
                </span>
              </div>
            )}
            {listing.coordinates && (
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="font-medium text-gray-600">Souřadnice:</span>
                <span className="text-gray-900 font-mono text-xs">
                  {listing.coordinates.lat.toFixed(6)}, {listing.coordinates.lng.toFixed(6)}
                </span>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="animate-pulse">
            <div className="h-6 bg-gray-200 rounded mb-6"></div>
            <div className="space-y-4">
              <div className="flex justify-between">
                <div className="h-4 bg-gray-200 rounded w-20"></div>
                <div className="h-4 bg-gray-200 rounded w-32"></div>
              </div>
              <div className="flex justify-between">
                <div className="h-4 bg-gray-200 rounded w-16"></div>
                <div className="h-4 bg-gray-200 rounded w-28"></div>
              </div>
            </div>
          </div>
        </div>
      ),
    },
  ]

  // Create header content for the Layout
  const headerContent = (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-2 sm:gap-4">
        <button onClick={() => navigate('/listings')} className="p-1.5 sm:p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md">
          <ArrowLeft size={18} className="sm:w-5 sm:h-5" />
        </button>
        <h1 className="text-lg sm:text-2xl font-semibold text-gray-900">Detail inzerátu</h1>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => navigate(`/listings/${listingId}/edit`)}
          className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-1.5 sm:py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm sm:text-base"
        >
          <Edit size={16} className="sm:w-5 sm:h-5" />
          <span className="hidden sm:inline">Upravit</span>
          <span className="sm:hidden">Upravit</span>
        </button>
        <button
          onClick={handleDelete}
          className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-1.5 sm:py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm sm:text-base"
        >
          <Trash2 size={16} className="sm:w-5 sm:h-5" />
          <span className="hidden sm:inline">Smazat</span>
          <span className="sm:hidden">Smazat</span>
        </button>
      </div>
    </div>
  )

  if (loading) {
    return (
      <Layout headerContent={headerContent}>
        <Seo title="Detail inzerátu" description="Detail inzerátu v administraci Realitní Jestřáb." canonicalUrl={typeof window !== 'undefined' ? window.location.href : undefined} />
        <div className="flex justify-center items-center h-64">Načítání...</div>
      </Layout>
    )
  }

  if (error) {
    return (
      <Layout headerContent={headerContent}>
        <Seo title="Detail inzerátu" description="Detail inzerátu v administraci Realitní Jestřáb." canonicalUrl={typeof window !== 'undefined' ? window.location.href : undefined} />
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <div className="text-red-600 mb-2">{error}</div>
            <button onClick={() => navigate('/listings')} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              Zpět na seznam
            </button>
          </div>
        </div>
      </Layout>
    )
  }

  if (!listing) {
    return (
      <Layout headerContent={headerContent}>
        <Seo title="Detail inzerátu" description="Detail inzerátu v administraci Realitní Jestřáb." canonicalUrl={typeof window !== 'undefined' ? window.location.href : undefined} />
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <div className="text-gray-600 mb-2">Inzerát nebyl nalezen</div>
            <button onClick={() => navigate('/listings')} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              Zpět na seznam
            </button>
          </div>
        </div>
      </Layout>
    )
  }

  const activeTabData = tabs.find((tab) => tab.id === activeTab)
  const listingTitle = listing?.address ? `Inzerát: ${listing.address}` : 'Detail inzerátu'
  const listingDescription = listing
    ? `Detail inzerátu ${listing.type || ''} ${listing.layout || ''} ${listing.m2 ? `${listing.m2} m²` : ''} v administraci Realitní Jestřáb.`
    : 'Detail inzerátu v administraci Realitní Jestřáb.'

  return (
    <Layout headerContent={headerContent}>
      <Seo title={listingTitle} description={listingDescription.trim()} canonicalUrl={typeof window !== 'undefined' ? window.location.href : undefined} />
      {/* Content */}
      <div className="px-4 py-6">
        {/* Tabs */}
        <div className="mb-6">
          <div className="flex gap-2 overflow-x-auto sm:flex-wrap border-b border-gray-200">
            {tabs.map((tab) => {
              const Icon = tab.icon
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium rounded-t-lg transition-all duration-200 whitespace-nowrap ${
                    activeTab === tab.id ? 'bg-white text-purple-700 border-b-2 border-purple-700' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{tab.name}</span>
                  <span className="sm:hidden">{tab.name.split(' ')[0]}</span>
                </button>
              )
            })}
          </div>
        </div>

        {/* Tab Content */}
        <div className="min-h-96">{activeTabData?.content}</div>
      </div>
    </Layout>
  )
}

export default ListingDetail
